import userService from "@/services/userService";
import type {
  User,
  PaginatedResponse,
  Pagination,
  UserListParams,
  CreateUserRequest,
  UpdateUserRequest,
} from "@/models/user.model";

type State = {
  loading: boolean;
  users: User[];
  pagination: Pagination | null;
  currentUser: User | null;
  listParams: UserListParams;
};

const state: State = {
  loading: false,
  users: [],
  pagination: null,
  currentUser: null,
  listParams: {
    page: 1,
    pageSize: 10,
    search: "",
    active: null,
    includeDeleted: false,
  },
};

const mutations = {
  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },
  setUsers(state: State, response: PaginatedResponse<User>) {
    state.users = response.items;
    state.pagination = response.pagination;
  },
  setCurrentUser(state: State, user: User | null) {
    state.currentUser = user;
  },
  setListParams(state: State, params: Partial<UserListParams>) {
    state.listParams = { ...state.listParams, ...params };
  },
  resetListParams(state: State) {
    state.listParams = {
      page: 1,
      pageSize: 10,
      search: "",
      active: null,
      includeDeleted: false,
    };
  },
};

const getters = {
  loading: (state: State) => state.loading,
  users: (state: State) => state.users,
  pagination: (state: State) => state.pagination,
  currentUser: (state: State) => state.currentUser,
  listParams: (state: State) => state.listParams,
};

const actions = {
  async fetchUsers({
    commit,
    state,
    dispatch,
  }: {
    commit: any;
    state: State;
    dispatch: any;
  }) {
    commit("setLoading", true);
    try {
      const response = await userService.getUsers(state.listParams);
      commit("setUsers", response);
      return response;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async fetchUser(
    { commit, dispatch }: { commit: any; dispatch: any },
    id: string
  ) {
    commit("setLoading", true);
    try {
      const user = await userService.getUser(id);
      commit("setCurrentUser", user);
      return user;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async createUser(
    { dispatch }: { dispatch: any },
    data: CreateUserRequest
  ) {
    try {
      const user = await userService.createUser(data);
      await dispatch("notificator/success", "user-created", { root: true });
      return user;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async updateUser(
    { dispatch }: { dispatch: any },
    { id, data }: { id: string; data: UpdateUserRequest }
  ) {
    try {
      const user = await userService.updateUser(id, data);
      await dispatch("notificator/success", "user-updated", { root: true });
      return user;
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  async deleteUser(
    { dispatch }: { dispatch: any },
    { id, permanent = false }: { id: string; permanent?: boolean }
  ) {
    try {
      await userService.deleteUser(id, permanent);
      await dispatch("notificator/success", "user-deleted", { root: true });
      await dispatch("fetchUsers");
    } catch (error) {
      await dispatch("notificator/apiError", error, { root: true });
      throw error;
    }
  },

  updateListParams(
    { commit, dispatch }: { commit: any; dispatch: any },
    params: Partial<UserListParams>
  ) {
    commit("setListParams", params);
    return dispatch("fetchUsers");
  },
};

export default { namespaced: true, state, mutations, getters, actions };
